/**
 * Legal Document Templates — Slash Command Definitions
 * Used by the /template command in the chat interface.
 * Each template defines fields and a prompt builder.
 */

export interface TemplateField {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'select';
  required?: boolean;
  placeholder?: string;
  options?: string[]; // for select type
}

export interface LegalTemplate {
  id: string;
  name: string;
  category: string;
  icon: string;
  description: string;
  fields: TemplateField[];
  buildPrompt: (data: Record<string, string>) => string;
}

export const LEGAL_TEMPLATES: LegalTemplate[] = [
  // ═══ CRIMINAL ═══
  {
    id: 'bail-regular',
    name: 'Bail Application (Regular)',
    category: 'Criminal',
    icon: '⚖️',
    description: 'Regular bail under Section 483 BNSS',
    fields: [
      { id: 'accusedName', label: 'Accused Name', type: 'text', required: true, placeholder: 'Full name of accused' },
      { id: 'firNo', label: 'FIR / Crime No.', type: 'text', required: true, placeholder: 'e.g. 123/2026, PS Shivaji Nagar' },
      { id: 'sections', label: 'Sections Charged', type: 'text', required: true, placeholder: 'e.g. 302, 307 BNS' },
      { id: 'court', label: 'Court', type: 'text', required: true, placeholder: 'e.g. Sessions Court, Pune' },
      { id: 'facts', label: 'Brief Facts of Case', type: 'textarea', required: true, placeholder: 'What happened? When was accused arrested?' },
      { id: 'grounds', label: 'Grounds for Bail', type: 'textarea', placeholder: 'No criminal antecedents, cooperation with investigation, etc.' },
    ],
    buildPrompt: (d) => `Draft a Regular Bail Application under Section 483 BNSS with the following details:

Accused: ${d.accusedName}
FIR/Crime No: ${d.firNo}
Sections: ${d.sections}
Court: ${d.court}

Facts of the Case:
${d.facts}

Grounds for Bail:
${d.grounds || 'Enumerate appropriate legal grounds based on the facts'}

Please generate a complete, properly formatted bail application including:
1. Title with Court Name and Case Number
2. "IN THE MATTER OF" section with parties
3. "APPLICATION UNDER SECTION 483 BNSS FOR GRANT OF BAIL"
4. Brief facts of the case (professionally expanded)
5. Grounds for bail (numbered, legally argued with relevant case law if applicable)
6. Prayer clause
7. Verification
8. Place, Date, and Advocate signature block

Use formal legal language. Format as a court-ready document.`,
  },
  {
    id: 'bail-anticipatory',
    name: 'Bail Application (Anticipatory)',
    category: 'Criminal',
    icon: '🛡️',
    description: 'Anticipatory bail under Section 482 BNSS',
    fields: [
      { id: 'applicantName', label: 'Applicant Name', type: 'text', required: true, placeholder: 'Full name of applicant' },
      { id: 'firNo', label: 'FIR / Crime No.', type: 'text', placeholder: 'If FIR registered (or apprehension of)' },
      { id: 'sections', label: 'Sections', type: 'text', required: true, placeholder: 'e.g. 318, 319 BNS' },
      { id: 'court', label: 'Court', type: 'text', required: true, placeholder: 'Sessions Court / High Court' },
      { id: 'apprehension', label: 'Reason to Believe Arrest', type: 'textarea', required: true, placeholder: 'Why does the applicant believe they will be arrested?' },
      { id: 'grounds', label: 'Grounds for Anticipatory Bail', type: 'textarea', placeholder: 'False implication, no evidence, malicious complaint, etc.' },
    ],
    buildPrompt: (d) => `Draft an Anticipatory Bail Application under Section 482 BNSS:

Applicant: ${d.applicantName}
FIR/Crime No: ${d.firNo || 'Not yet registered / Apprehension of registration'}
Sections: ${d.sections}
Court: ${d.court}

Reason to believe arrest is imminent:
${d.apprehension}

Grounds:
${d.grounds || 'Provide appropriate legal grounds'}

Generate a complete anticipatory bail application with:
1. Title, court details, application number
2. Parties
3. "APPLICATION U/S 482 BNSS FOR ANTICIPATORY BAIL"
4. Facts and circumstances showing apprehension of arrest
5. Grounds (numbered, with legal reasoning)
6. Conditions applicant is willing to accept
7. Prayer clause
8. Verification and signature block

Formal legal language. Court-ready format.`,
  },

  // ═══ CIVIL — LEGAL NOTICES ═══
  {
    id: 'notice-cheque-bounce',
    name: 'Legal Notice (Cheque Bounce - S.138 NI Act)',
    category: 'Civil',
    icon: '📨',
    description: 'Demand notice under Section 138 Negotiable Instruments Act',
    fields: [
      { id: 'senderName', label: 'Sender (Payee) Name', type: 'text', required: true, placeholder: 'Who is sending the notice' },
      { id: 'recipientName', label: 'Recipient (Drawer) Name', type: 'text', required: true, placeholder: 'Who issued the bounced cheque' },
      { id: 'chequeNo', label: 'Cheque Number', type: 'text', required: true, placeholder: 'Cheque number' },
      { id: 'amount', label: 'Amount (₹)', type: 'text', required: true, placeholder: 'e.g. 5,00,000' },
      { id: 'bank', label: 'Bank & Branch', type: 'text', required: true, placeholder: 'Drawn on which bank' },
      { id: 'returnReason', label: 'Reason for Return', type: 'text', placeholder: 'Insufficient funds / Account closed / etc.' },
    ],
    buildPrompt: (d) => `Draft a Legal Notice under Section 138 of the Negotiable Instruments Act, 1881:

Sender (Payee): ${d.senderName}
Recipient (Drawer): ${d.recipientName}
Cheque No: ${d.chequeNo}
Amount: ₹${d.amount}
Bank: ${d.bank}
Reason for dishonour: ${d.returnReason || 'Insufficient funds'}

Generate a complete legal notice including:
1. "LEGAL NOTICE" header with date
2. From/To with full addresses (use placeholder for addresses)
3. Subject line referencing S.138 NI Act
4. Recital of facts: the cheque was issued, presented, and dishonoured
5. Demand: pay within 15 days from receipt of notice
6. Warning: criminal complaint under S.138 NI Act will be filed if not paid
7. Reference to Section 138, 141, and 142 of the NI Act
8. "Under instructions of my client" closing
9. Advocate signature block

Use formal legal notice language. Must clearly state the 15-day statutory period.`,
  },
  {
    id: 'notice-recovery',
    name: 'Legal Notice (Recovery of Money)',
    category: 'Civil',
    icon: '💰',
    description: 'Demand notice for recovery of dues',
    fields: [
      { id: 'senderName', label: 'Sender Name', type: 'text', required: true },
      { id: 'recipientName', label: 'Recipient Name', type: 'text', required: true },
      { id: 'amount', label: 'Amount Due (₹)', type: 'text', required: true, placeholder: 'e.g. 2,50,000' },
      { id: 'nature', label: 'Nature of Debt', type: 'text', required: true, placeholder: 'Loan / Service payment / Goods supplied / etc.' },
      { id: 'facts', label: 'Brief Facts', type: 'textarea', required: true, placeholder: 'When was money lent/service provided? Any agreement?' },
    ],
    buildPrompt: (d) => `Draft a Legal Notice for Recovery of Money:

Sender: ${d.senderName}
Recipient: ${d.recipientName}
Amount: ₹${d.amount}
Nature of debt: ${d.nature}

Facts: ${d.facts}

Generate a formal legal notice demanding payment including:
1. Header with date, reference number
2. From/To (use placeholder addresses)
3. Subject: Demand for payment
4. Facts of the transaction
5. Demand: pay within 15 days
6. Consequence: civil suit for recovery + interest will be filed
7. Advocate signature

Professional legal language.`,
  },
  {
    id: 'notice-eviction',
    name: 'Legal Notice (Eviction)',
    category: 'Civil',
    icon: '🏠',
    description: 'Notice to tenant for eviction',
    fields: [
      { id: 'landlordName', label: 'Landlord Name', type: 'text', required: true },
      { id: 'tenantName', label: 'Tenant Name', type: 'text', required: true },
      { id: 'property', label: 'Property Address', type: 'text', required: true, placeholder: 'Full property description' },
      { id: 'reason', label: 'Reason for Eviction', type: 'select', required: true, options: ['Non-payment of rent', 'Subletting without permission', 'Nuisance/Damage', 'Bona fide personal use', 'Lease expired'] },
      { id: 'details', label: 'Additional Details', type: 'textarea', placeholder: 'Months of unpaid rent, specific violations, etc.' },
    ],
    buildPrompt: (d) => `Draft a Legal Notice for Eviction:

Landlord: ${d.landlordName}
Tenant: ${d.tenantName}
Property: ${d.property}
Reason: ${d.reason}
Details: ${d.details || ''}

Generate a legal eviction notice including:
1. Header with date
2. From/To
3. Subject: Notice to vacate premises
4. Facts: tenancy details, violation/reason
5. Legal basis (relevant Rent Control Act provisions)
6. Demand: vacate within 30 days / pay arrears within 15 days
7. Consequence: eviction proceedings will be initiated
8. Advocate signature

Formal legal language with applicable statutory references.`,
  },

  // ═══ DRAFTING ═══
  {
    id: 'affidavit-general',
    name: 'Affidavit (General Purpose)',
    category: 'Drafting',
    icon: '📜',
    description: 'General affidavit on stamp paper',
    fields: [
      { id: 'deponentName', label: 'Deponent Name', type: 'text', required: true, placeholder: 'Person making the affidavit' },
      { id: 'age', label: 'Age', type: 'text', required: true, placeholder: 'e.g. 35 years' },
      { id: 'address', label: 'Address', type: 'text', required: true },
      { id: 'purpose', label: 'Purpose of Affidavit', type: 'text', required: true, placeholder: 'e.g. Name change, Lost document, Income declaration' },
      { id: 'facts', label: 'Facts to State (one per line)', type: 'textarea', required: true, placeholder: 'I state that...\nI further state that...' },
    ],
    buildPrompt: (d) => `Draft a General Purpose Affidavit:

Deponent: ${d.deponentName}
Age: ${d.age}
Address: ${d.address}
Purpose: ${d.purpose}

Facts to include:
${d.facts}

Generate a complete affidavit including:
1. Title: "AFFIDAVIT"
2. Opening: "I, [name], aged [age], resident of [address], do hereby solemnly affirm and state as under:"
3. Numbered paragraphs with the facts
4. Closing: "I say that the above statements are true and correct to the best of my knowledge and belief..."
5. Verification clause (as per CPC Order 19)
6. Place, Date
7. Signature block: "DEPONENT"
8. Note: "Verified at [place] on [date]. The contents are true. No part is false."

Standard affidavit format on stamp paper.`,
  },
  {
    id: 'will',
    name: 'Will / Testament',
    category: 'Drafting',
    icon: '📋',
    description: 'Last Will and Testament',
    fields: [
      { id: 'testatorName', label: 'Testator Name', type: 'text', required: true, placeholder: 'Person making the will' },
      { id: 'age', label: 'Age', type: 'text', required: true },
      { id: 'beneficiaries', label: 'Beneficiaries & Shares', type: 'textarea', required: true, placeholder: 'Son - 50% of property\nDaughter - 50% of property\nWife - gold ornaments + house' },
      { id: 'properties', label: 'Properties/Assets', type: 'textarea', required: true, placeholder: 'House at...\nFlat at...\nBank FD of...\nGold jewellery...' },
      { id: 'executor', label: 'Executor Name', type: 'text', required: true, placeholder: 'Who will execute the will' },
    ],
    buildPrompt: (d) => `Draft a Last Will and Testament under Indian Succession Act / Hindu Succession Act:

Testator: ${d.testatorName}, Age: ${d.age}

Properties/Assets:
${d.properties}

Beneficiaries & Distribution:
${d.beneficiaries}

Executor: ${d.executor}

Generate a complete will including:
1. Title: "LAST WILL AND TESTAMENT"
2. Declaration: "I, [name], aged [age], in sound mind..."
3. Revocation clause (revoking all prior wills)
4. Family details
5. Specific bequests (numbered, property by property)
6. Residuary clause
7. Appointment of executor
8. Conditions (if any)
9. Attestation clause
10. Signature block for testator + 2 witnesses

Proper legal format. Clear and unambiguous language.`,
  },
  {
    id: 'vakalatnama',
    name: 'Vakalatnama',
    category: 'Drafting',
    icon: '🤝',
    description: 'Advocate authorization / Power to appear',
    fields: [
      { id: 'clientName', label: 'Client Name', type: 'text', required: true },
      { id: 'advocateName', label: 'Advocate Name', type: 'text', required: true },
      { id: 'barCouncil', label: 'Bar Council Enrolment No.', type: 'text', required: true, placeholder: 'e.g. MH/1234/2020' },
      { id: 'court', label: 'Court', type: 'text', required: true, placeholder: 'e.g. District Court, Pune' },
      { id: 'caseDetails', label: 'Case Details', type: 'text', placeholder: 'Case No. / Subject matter' },
    ],
    buildPrompt: (d) => `Draft a Vakalatnama (Power to Appear):

Client: ${d.clientName}
Advocate: ${d.advocateName}
Enrolment No: ${d.barCouncil}
Court: ${d.court}
Case: ${d.caseDetails || 'All matters'}

Generate a standard Vakalatnama format including:
1. Title: "VAKALATNAMA"
2. "I/We, [client name], do hereby appoint and authorize..."
3. Advocate details with enrolment number
4. Authorization clauses (appear, plead, file, compromise, withdraw, receive)
5. Court and case reference
6. Date and signatures (client + advocate)
7. Witness block (2 witnesses)

Standard bar council format.`,
  },
  {
    id: 'poa-general',
    name: 'Power of Attorney (General)',
    category: 'Drafting',
    icon: '✍️',
    description: 'General Power of Attorney',
    fields: [
      { id: 'principalName', label: 'Principal (Grantor) Name', type: 'text', required: true },
      { id: 'agentName', label: 'Agent (Attorney) Name', type: 'text', required: true },
      { id: 'purpose', label: 'Purpose / Scope', type: 'textarea', required: true, placeholder: 'Manage property at...\nOperate bank account...\nSell flat at...' },
      { id: 'duration', label: 'Duration', type: 'select', options: ['Until revoked', '1 year', '2 years', '5 years', 'For specific transaction only'] },
    ],
    buildPrompt: (d) => `Draft a General Power of Attorney:

Principal: ${d.principalName}
Attorney/Agent: ${d.agentName}
Duration: ${d.duration || 'Until revoked'}

Scope/Purpose:
${d.purpose}

Generate a complete GPA including:
1. Title: "GENERAL POWER OF ATTORNEY"
2. "KNOW ALL MEN BY THESE PRESENTS" opening
3. Description of principal and attorney
4. Specific powers granted (numbered clauses)
5. General authority clause
6. Ratification clause
7. Duration/validity
8. Revocation provisions
9. Execution clause
10. Signature block: Principal + 2 witnesses
11. Note about registration requirement

Proper legal format suitable for Sub-Registrar registration.`,
  },

  // ═══ PROPERTY ═══
  {
    id: 'rent-agreement',
    name: 'Rent Agreement / Leave & License',
    category: 'Property',
    icon: '🏢',
    description: 'Standard 11-month leave and license agreement',
    fields: [
      { id: 'landlordName', label: 'Licensor (Landlord)', type: 'text', required: true },
      { id: 'tenantName', label: 'Licensee (Tenant)', type: 'text', required: true },
      { id: 'property', label: 'Property Description', type: 'text', required: true, placeholder: 'Flat No., Building, Area, City' },
      { id: 'rent', label: 'Monthly Rent (₹)', type: 'text', required: true, placeholder: 'e.g. 25,000' },
      { id: 'deposit', label: 'Security Deposit (₹)', type: 'text', required: true, placeholder: 'e.g. 1,50,000' },
      { id: 'tenure', label: 'Period', type: 'select', options: ['11 months', '1 year', '2 years', '3 years'] },
    ],
    buildPrompt: (d) => `Draft a Leave and License Agreement (Rent Agreement):

Licensor: ${d.landlordName}
Licensee: ${d.tenantName}
Property: ${d.property}
Monthly Rent: ₹${d.rent}
Security Deposit: ₹${d.deposit}
Period: ${d.tenure || '11 months'}

Generate a complete agreement including:
1. Title: "LEAVE AND LICENSE AGREEMENT"
2. Date and parties
3. Recitals (WHEREAS clauses)
4. License grant clause
5. Term/Duration
6. License fee (rent) and payment terms
7. Security deposit terms and refund conditions
8. Maintenance charges
9. Permitted use
10. Restrictions (no subletting, no structural changes)
11. Termination clause (1 month notice)
12. Responsibilities (repairs, utilities)
13. Dispute resolution
14. Registration clause
15. Signatures: Both parties + 2 witnesses

Standard Maharashtra/Indian format. Suitable for registration.`,
  },

  // ═══ FAMILY ═══
  {
    id: 'divorce-mutual',
    name: 'Divorce Petition (Mutual Consent)',
    category: 'Family',
    icon: '💔',
    description: 'Joint petition under Section 13-B Hindu Marriage Act',
    fields: [
      { id: 'husbandName', label: 'Husband Name', type: 'text', required: true },
      { id: 'wifeName', label: 'Wife Name', type: 'text', required: true },
      { id: 'marriageDate', label: 'Date of Marriage', type: 'text', required: true, placeholder: 'e.g. 15 January 2018' },
      { id: 'separationDate', label: 'Date of Separation', type: 'text', required: true, placeholder: 'e.g. 1 March 2025' },
      { id: 'terms', label: 'Settlement Terms', type: 'textarea', required: true, placeholder: 'Alimony amount, property division, child custody...' },
    ],
    buildPrompt: (d) => `Draft a Joint Petition for Divorce by Mutual Consent under Section 13-B of the Hindu Marriage Act, 1955:

Husband (Petitioner No.1): ${d.husbandName}
Wife (Petitioner No.2): ${d.wifeName}
Marriage Date: ${d.marriageDate}
Separation Date: ${d.separationDate}

Settlement Terms:
${d.terms}

Generate a complete joint petition including:
1. Title: Court, Case number placeholder
2. Parties (both as petitioners)
3. "JOINT PETITION U/S 13-B HINDU MARRIAGE ACT, 1955"
4. Facts: marriage details, separation for more than 1 year, mutual consent
5. Statement: marriage has irretrievably broken down
6. Settlement terms (maintenance, property, custody)
7. Statement: no coercion or undue influence
8. Prayer: dissolve the marriage
9. Verification by both parties
10. Signatures of both petitioners and advocates

Formal family court format.`,
  },

  // ═══ CONSUMER ═══
  {
    id: 'consumer-complaint',
    name: 'Consumer Complaint',
    category: 'Consumer',
    icon: '🛒',
    description: 'Complaint under Consumer Protection Act, 2019',
    fields: [
      { id: 'complainantName', label: 'Complainant Name', type: 'text', required: true },
      { id: 'oppositeParty', label: 'Opposite Party (Company/Seller)', type: 'text', required: true },
      { id: 'product', label: 'Product/Service', type: 'text', required: true, placeholder: 'e.g. Samsung Washing Machine / Jio Broadband' },
      { id: 'deficiency', label: 'Deficiency/Defect', type: 'textarea', required: true, placeholder: 'What went wrong? When? How much loss?' },
      { id: 'relief', label: 'Relief Sought (₹)', type: 'text', required: true, placeholder: 'Refund + Compensation amount' },
    ],
    buildPrompt: (d) => `Draft a Consumer Complaint under the Consumer Protection Act, 2019:

Complainant: ${d.complainantName}
Opposite Party: ${d.oppositeParty}
Product/Service: ${d.product}

Deficiency/Defect:
${d.deficiency}

Relief Sought: ₹${d.relief}

Generate a complete consumer complaint including:
1. Title: "BEFORE THE DISTRICT CONSUMER DISPUTES REDRESSAL COMMISSION"
2. Parties (Complainant vs Opposite Party)
3. "CONSUMER COMPLAINT UNDER SECTION 35 OF THE CONSUMER PROTECTION ACT, 2019"
4. Facts (numbered paragraphs): purchase details, deficiency, communications with seller
5. Cause of action
6. Jurisdiction clause
7. Limitation (within 2 years)
8. Relief sought (itemized: refund, compensation, litigation cost)
9. Prayer
10. Verification
11. List of documents

Standard consumer forum format.`,
  },
];

// Helper: get templates grouped by category
export function getTemplatesByCategory(): Record<string, LegalTemplate[]> {
  return LEGAL_TEMPLATES.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {} as Record<string, LegalTemplate[]>);
}

// Helper: search templates
export function searchTemplates(query: string): LegalTemplate[] {
  const q = query.toLowerCase();
  return LEGAL_TEMPLATES.filter(
    t => t.name.toLowerCase().includes(q) || t.category.toLowerCase().includes(q) || t.description.toLowerCase().includes(q)
  );
}
